/**
 * Shared Zod schema for the Kundali / birth-details form.
 *
 * Used by:
 *   - apps/mobile/components/kundali/BirthForm.tsx
 *   - apps/web/src/components/BirthDetailsForm.tsx
 *
 * Mirrors the validation logic that was previously duplicated across both
 * surfaces.  Fields match the FastAPI KundaliRequest schema in types.ts so
 * `toKundaliRequest()` is a trivial rename (birthDate → birth_date, etc.).
 */

import { z } from "zod";

const MIN_BIRTH_YEAR = 1900;

/** YYYY-MM-DD, not in the future, not before 1900. */
export const birthDateSchema = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Please enter a valid date (YYYY-MM-DD)")
  .refine(
    (s) => {
      const d = new Date(s);
      return !isNaN(d.getTime());
    },
    { message: "Please enter a valid date" },
  )
  .refine(
    (s) => {
      const d = new Date(s);
      return d.getFullYear() >= MIN_BIRTH_YEAR;
    },
    { message: `Birth year must be ${MIN_BIRTH_YEAR} or later` },
  )
  .refine(
    (s) => {
      const d = new Date(s);
      return d <= new Date();
    },
    { message: "Birth date cannot be in the future" },
  );

/** HH:MM in 24-hour format, or null for "unknown time". */
export const birthTimeSchema = z
  .union([
    z
      .string()
      .trim()
      .regex(
        /^([01]\d|2[0-3]):[0-5]\d$/,
        "Please enter a valid time (HH:MM)",
      ),
    z.null(),
  ])
  .describe("24-hour HH:MM or null if unknown");

/** Non-empty place name — city autocomplete resolves lat/lng separately. */
export const birthPlaceSchema = z
  .string()
  .trim()
  .min(2, "Please enter your birth place");

/**
 * Full Kundali form input schema (camelCase field names matching form state).
 *
 * Use `toKundaliRequest()` to convert to the snake_case `KundaliRequest`
 * shape the backend expects.
 */
export const kundaliFormSchema = z.object({
  name: z.string().trim().min(1, "Please enter your name").max(100),
  birthDate: birthDateSchema,
  birthTime: birthTimeSchema,
  birthPlace: birthPlaceSchema,
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  timezone: z.string().optional(),
  gender: z.enum(["male", "female", "other"]).optional(),
  language: z.string().optional(),
});

export type KundaliFormInput = z.infer<typeof kundaliFormSchema>;

/**
 * Convert validated form input to the backend-ready KundaliRequest shape.
 * Passes through lat/lng/timezone only when the user picked a place via
 * the autocomplete (those fields are optional in KundaliRequest).
 */
export function toKundaliRequest(input: KundaliFormInput) {
  return {
    name: input.name,
    birth_date: input.birthDate,
    birth_time: input.birthTime ?? null,
    birth_place: input.birthPlace,
    ...(input.latitude != null && { latitude: input.latitude }),
    ...(input.longitude != null && { longitude: input.longitude }),
    ...(input.timezone != null && { timezone: input.timezone }),
    ...(input.gender != null && { gender: input.gender }),
    ...(input.language != null && { language: input.language }),
  };
}
